import { useCallback, useEffect, useMemo } from "react";
import { Company } from "@forgepoint/shared";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { EntitySelector } from "@/components/EntitySelector";

interface CompanySelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (companyId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "default" | "sm";
}

/**
 * Company selector component that provides a searchable dropdown for selecting companies.
 * Uses the generic EntitySelector with company-specific configuration.
 *
 * Fetches initial data on mount and ensures the selected company is in the cache.
 */
export function CompanySelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select a company...",
  className,
  disabled = false,
  size = "default",
}: CompanySelectorProps) {
  const { companies, listLoading, fetchCompanies, fetchCompany } =
    useCompaniesStore();

  // Ensure selected company is in cache on mount
  useEffect(() => {
    if (currentlySelectedId && !companies.has(currentlySelectedId)) {
      fetchCompany(currentlySelectedId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEntities = useCallback(
    async (search?: string) => {
      const result = await fetchCompanies({ search });
      return result.ids;
    },
    [fetchCompanies],
  );

  const getDisplayName = useCallback((company: Company) => {
    return company.name;
  }, []);

  const duplicatedNames = useMemo(() => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const company of companies.values()) {
      const name = company.name.toLowerCase();
      if (seen.has(name)) {
        duplicates.add(name);
        continue;
      }
      seen.add(name);
    }
    return duplicates;
  }, [companies]);

  const getSecondaryInfo = useCallback(
    (company: Company) => {
      const parts = [];
      if (duplicatedNames.has(company.name.toLowerCase())) {
        parts.push(`ID ${company.id}`);
      }
      if (company.phone) {
        parts.push(company.phone);
      }
      if (company.address?.city && company.address?.state) {
        parts.push(`${company.address.city}, ${company.address.state}`);
      } else if (company.address?.city) {
        parts.push(company.address.city);
      } else if (company.address?.state) {
        parts.push(company.address.state);
      }
      return parts.join(" • ");
    },
    [duplicatedNames],
  );

  const renderExtraInfo = useCallback(
    (company: Company) =>
      company.address?.address_line_1 ? (
        <div className="text-xs text-muted-foreground line-clamp-1">
          {company.address.address_line_1}
        </div>
      ) : null,
    [],
  );

  return (
    <EntitySelector<Company>
      entities={companies}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      renderExtraInfo={renderExtraInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search companies..."
      className={className}
      disabled={disabled}
      size={size}
      entityLabel="companies"
    />
  );
}
